/**
 * 管理员系统设置页面
 * 管理 7 个系统级别的搜索配置项
 */
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import { toast } from '../../store/toastStore';

interface SettingItem {
  key: string;
  label: string;
  desc: string;
  type: 'text' | 'select' | 'number';
  options?: { value: string; label: string }[];
}

const SETTINGS: SettingItem[] = [
  { key: 'search_api_key', label: '系统搜索 API Key', desc: '供所有用户共用的 Bing API Key', type: 'text' },
  {
    key: 'default_engine', label: '默认搜索引擎', desc: '用户未配置时使用的搜索引擎',
    type: 'select',
    options: [
      { value: 'bing', label: 'Bing' },
      { value: 'duckduckgo', label: 'DuckDuckGo' },
      { value: 'both', label: '自动（Bing + 备用）' },
    ],
  },
  {
    key: 'default_search_depth', label: '默认搜索深度', desc: '用户未配置时使用的搜索深度',
    type: 'select',
    options: [
      { value: 'quick', label: '快速' },
      { value: 'normal', label: '标准' },
      { value: 'deep', label: '深度' },
    ],
  },
  { key: 'default_max_results', label: '默认搜索结果数', desc: '用户未配置时的搜索结果数量（1-20）', type: 'number' },
  { key: 'min_relevance_score', label: '最低相关性阈值', desc: '低于此值的结果被过滤（0.0-1.0）', type: 'number' },
  { key: 'max_reference_materials', label: '最大参考资料数', desc: '单次生成最多引用的参考资料', type: 'number' },
  { key: 'cache_ttl', label: '缓存有效期', desc: '搜索缓存有效天数', type: 'number' },
  {
    key: 'chart_engine', label: '图表生成引擎', desc: '<!-- img:generate --> 标记使用的绘图引擎',
    type: 'select',
    options: [
      { value: 'mermaid', label: 'Mermaid（默认）' },
    ],
  },
  { key: 'default_word_quota', label: '新用户默认字数', desc: '注册自动赠送的字数配额（留空=不限）', type: 'number' },
];

const AdminSettings: React.FC = () => {
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSettings();
      setForm(res.data);
    } catch (e) {
      console.error('加载系统设置失败', e);
      toast.error('加载系统设置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(form);
      toast.success('系统设置已更新');
    } catch (e) {
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">加载中...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">系统设置</h1>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-background border-b border-border">
            <tr>
              <th className="text-left px-6 py-3 font-medium text-foreground">配置项</th>
              <th className="text-left px-6 py-3 font-medium text-foreground">当前值</th>
              <th className="text-left px-6 py-3 font-medium text-foreground">说明</th>
            </tr>
          </thead>
          <tbody>
            {SETTINGS.map(s => (
              <tr key={s.key} className="border-b border-border">
                <td className="px-6 py-3 font-medium text-foreground whitespace-nowrap">{s.label}</td>
                <td className="px-6 py-3">
                  {s.key === 'search_api_key' ? (
                    <div className="flex gap-2 items-center">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={form[s.key] || ''}
                        onChange={e => setForm(f => ({ ...f, [s.key]: e.target.value }))}
                        className="w-48 border border-border rounded px-2 py-1.5 text-sm font-mono"
                        placeholder="未配置"
                      />
                      <button onClick={() => setShowApiKey(!showApiKey)}
                        className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap">
                        {showApiKey ? '隐藏' : '显示'}
                      </button>
                    </div>
                  ) : s.type === 'select' && s.options ? (
                    <select
                      value={form[s.key] || ''}
                      onChange={e => setForm(f => ({ ...f, [s.key]: e.target.value }))}
                      className="border border-border rounded px-2 py-1.5 text-sm"
                    >
                      {s.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={form[s.key] || ''}
                      onChange={e => setForm(f => ({ ...f, [s.key]: e.target.value }))}
                      className="w-24 border border-border rounded px-2 py-1.5 text-sm text-center"
                    />
                  )}
                </td>
                <td className="px-6 py-3 text-muted-foreground text-xs">{s.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="px-6 py-4 bg-accent border-t border-border flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2 text-sm font-medium rounded-lg ${
              saving ? 'bg-blue-300 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
